import { mat2d } from 'gl-matrix';
import { type BorderAlignmentTypes, type ColorParams, type LineCapTypes, StrokeType } from '@bynder-studio/misc';
import type { IStructuredGlyph } from '@bynder-studio/structured-text';
import { RegionTypes } from '../../Enums/RegionTypes';
import { getSrcDestTransform, transformRegion } from '../../Helpers/compositorUtils';
import type { ClipSoftness } from '../../types';
import type { AnimatableTarget } from '../Animations/AnimationChanges';
import { BlurEffect } from '../Effects/BlurEffect';
import { DropShadowEffect } from '../Effects/DropShadowEffect';
import type { BlendMode } from '../Shared/BlendMode';
import { Box } from '../Shared/Box';
import { Dimension } from '../Shared/Dimension';
import type { DropShadow } from '../Shared/DropShadow';
import type { Mask } from '../Shared/Mask';
import { Position } from '../Shared/Position';
import { Region } from '../Shared/Region';
import type { BaseCompElement } from './Elements/BaseCompElement';

export abstract class TextCompParams implements AnimatableTarget {
    abstract readonly kind: 'svg' | 'shape' | 'group';

    id!: string | number;

    name?: string;

    renderOrder!: number;

    parent: TextGroupParams | null = null;

    boundingBox!: Box;

    contentBox!: Box;

    hidden = false;

    opacity = 1.0;

    rotation = 0.0;

    scale = 1.0;

    blur = 0.0;

    clipPath?: string | null;

    clipSoftness?: ClipSoftness;

    dropShadow?: DropShadow | null;

    mask?: Mask | null;

    usedAsInternalMask = false;

    requireTempLayer = false;

    isInAnimation = false;

    blendMode?: BlendMode | null;

    motionBlur = false;

    translatedX = 0;

    translatedY = 0;

    isAssetLoading = false;

    remove = false;

    getAbsBoxPosition(): Position {
        let position = this.boundingBox.position;

        if (this.parent !== null) {
            position = position.add(this.parent.getAbsContentPosition());
        }

        return position;
    }

    getAbsContentPosition(): Position {
        return this.getAbsBoxPosition().add(this.contentBox.position);
    }

    getRegion(type: RegionTypes = RegionTypes.TRANSFORMED): Region {
        let region = new Region(0, 0, this.contentBox.dimension.width, this.contentBox.dimension.height);

        if (type === RegionTypes.TRANSFORMED || type === RegionTypes.EFFECT) {
            region = this.transformAndExtend(region, type);
        }

        return region;
    }

    protected transformAndExtend(contentRegion: Region, type: RegionTypes): Region {
        let region = contentRegion;

        if (this.rotation !== 0 || this.scale !== 1) {
            region = transformRegion(this.getScaleAndRotationTransform(), region);
        }

        if (type === RegionTypes.EFFECT && (!!this.dropShadow || this.blur !== 0)) {
            region = DropShadowEffect.getRegionOfInterest(region, this as never);
            region = BlurEffect.getRegionOfInterest(region, this as never);
        }

        return region;
    }

    private getScaleAndRotationTransform(): mat2d {
        if (this.rotation === 0 && this.scale === 1) {
            return mat2d.create();
        }

        const centerX = this.boundingBox.dimension.width / 2 - this.contentBox.position.x;
        const centerY = this.boundingBox.dimension.height / 2 - this.contentBox.position.y;

        return getSrcDestTransform(centerX, centerY, this.rotation, this.scale, -centerX, -centerY);
    }
}

export class TextSvgParams extends TextCompParams {
    readonly kind = 'svg';

    path!: string;

    decorationPaths?: string[];

    horizontalPathScale = 1;

    verticalPathScale = 1;

    color!: ColorParams;

    pathPosition!: Position;

    strokeType = StrokeType.NONE;

    strokeWidth = 0;

    strokeColor: ColorParams | null = null;

    strokeGradientRegion: Region | null = null;

    colorGradientRegion: Region | null = null;

    punchOut = false;

    glyph?: IStructuredGlyph['data'];

    isGlyph = false;

    maskTarget: TextCompParams | null = null;
}

export class TextShapeParams extends TextCompParams {
    readonly kind = 'shape';

    path!: string;

    color!: ColorParams;

    pathPosition!: Position;

    borderColor?: ColorParams | null = null;

    borderWidth?: number | null = null;

    borderAlignment?: BorderAlignmentTypes | null = null;

    borderLineCap?: LineCapTypes | null = null;

    borderOf: TextShapeParams | null = null;
}

export class TextGroupParams extends TextCompParams {
    readonly kind = 'group';

    children: (TextCompParams | BaseCompElement)[] = [];

    getRegion(type: RegionTypes = RegionTypes.TRANSFORMED): Region {
        const regions: Region[] = [];

        this.children.forEach((child) => {
            const region = child.getRegion(RegionTypes.EFFECT);

            if (region) {
                regions.push(
                    region.getTranslation(
                        child.boundingBox.position.x + child.contentBox.position.x,
                        child.boundingBox.position.y + child.contentBox.position.y,
                    ),
                );
            }
        });

        if (!regions.length) {
            return new Region(0, 0, this.contentBox.dimension.width, this.contentBox.dimension.height);
        }

        const region = regions.reduce((union, current) => union.getUnion(current));

        return type === RegionTypes.UNTRANSFORMED ? region : this.transformAndExtend(region, type);
    }
}

export const layOutGroup = (group: TextGroupParams, children: TextCompParams[]) => {
    let tlX = Number.POSITIVE_INFINITY;
    let tlY = Number.POSITIVE_INFINITY;
    let brX = Number.NEGATIVE_INFINITY;
    let brY = Number.NEGATIVE_INFINITY;

    children.forEach((child) => {
        if (!child.contentBox) {
            return;
        }

        const { x, y } = child.contentBox.position;
        const { width, height } = child.contentBox.dimension;
        const bBoxX = child.boundingBox.position.getX();
        const bBoxY = child.boundingBox.position.getY();

        tlX = Math.min(x + bBoxX, tlX);
        tlY = Math.min(y + bBoxY, tlY);
        brX = Math.max(x + width + bBoxX, brX);
        brY = Math.max(y + height + bBoxY, brY);
    });

    if (
        tlX !== Number.POSITIVE_INFINITY &&
        brX !== Number.NEGATIVE_INFINITY &&
        tlY !== Number.POSITIVE_INFINITY &&
        brY !== Number.NEGATIVE_INFINITY
    ) {
        const dimension = new Dimension(brX - tlX, brY - tlY);

        group.contentBox = new Box(new Position(0, 0), dimension);
        group.boundingBox = new Box(new Position(tlX, tlY), dimension);
    } else {
        group.contentBox = new Box(new Position(0, 0), new Dimension(0, 0));
        group.boundingBox = new Box(new Position(0, 0), new Dimension(0, 0));
    }

    children.forEach((child) => {
        child.parent = group;
        child.boundingBox.position = child.boundingBox.position.subtract(group.boundingBox.position);
    });

    group.children = children;
};
